import React, { useState } from 'react';
import axios from '../api/axiosConfig';

const TIPOS_DOC = [
  ['CC', 'cédula de ciudadanía'], ['NIT', 'nit'], ['CE', 'cédula de extranjería'],
  ['PP', 'pasaporte'], ['PPT', 'permiso de permanencia temporal (PPT)'], ['TI', 'tarjeta de identidad']
];
const EXT_OK = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];

const FormularioInscripcionPrenda = ({ onClose, onSuccess, onOrigenResuelto }) => {
  const [placa, setPlaca] = useState('');
  const [consultando, setConsultando] = useState(false);
  const [info, setInfo] = useState(null);
  const [errorPlaca, setErrorPlaca] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [buscandoProp, setBuscandoProp] = useState(false);
  const [buscandoAcr, setBuscandoAcr] = useState(false);
  const [mandato, setMandato] = useState(null);
  const [otros, setOtros] = useState([]);
  const [f, setF] = useState({
    propietario: '', tipo_documento: 'CC', documento: '', correo: '', telefono: '', direccion: '',
    estado_persona: null, tiene_multas: false,
    representante_legal_nombre: '', representante_legal_tipo_documento: 'CC', representante_legal_documento: '',
    acreedor_tipo_documento: 'NIT', acreedor_documento: '', acreedor_nombre: '', acreedor_estado_persona: null,
    observaciones: ''
  });

  const set = (name, value) => setF(prev => ({ ...prev, [name]: value }));
  const up = (e) => set(e.target.name, e.target.value.toUpperCase());

  const consultarPlaca = async () => {
    const p = placa.trim().toUpperCase();
    if (p.length < 5) { alert('⚠️ Ingrese la placa del vehículo'); return; }
    setConsultando(true); setErrorPlaca(''); setInfo(null);
    try {
      const res = await axios.get('/api/tramites/inscripcion-prenda/lookup', { params: { placa: p }, timeout: 120000 });
      const d = res.data;
      setInfo(d);
      if (d.transito && d.tipo_tramite_id && onOrigenResuelto) onOrigenResuelto(d.transito.id, d.tipo_tramite_id);
      if (d.propietario) {
        setF(prev => ({
          ...prev,
          propietario: d.propietario.propietario || '', tipo_documento: d.propietario.tipo_documento || 'CC',
          documento: d.propietario.documento || '', correo: d.propietario.correo || '',
          telefono: d.propietario.telefono || '', direccion: d.propietario.direccion || ''
        }));
      }
    } catch (err) {
      setErrorPlaca(err.response?.data?.message || 'No se pudo consultar la placa');
    } finally {
      setConsultando(false);
    }
  };

  const buscarPropietario = async () => {
    if (!f.documento) { alert('⚠️ Ingrese el documento del propietario'); return; }
    setBuscandoProp(true);
    try {
      const res = await axios.get(`/api/tramites/owner/${f.documento}?tipo_documento_id=${f.tipo_documento}`);
      if (res.data) {
        setF(prev => ({
          ...prev,
          propietario: res.data.propietario || prev.propietario,
          correo: res.data.correo || prev.correo, telefono: res.data.telefono || prev.telefono,
          direccion: res.data.direccion || prev.direccion,
          estado_persona: res.data.estado_persona || null, tiene_multas: Boolean(res.data.tiene_multas)
        }));
        if (res.data.tiene_multas) alert('🚨 El propietario presenta multas en SIMIT. Se generará una novedad para el concesionario.');
      }
    } catch (err) {
      alert(err.response?.status === 404 ? 'ℹ️ Propietario no encontrado. Ingrese los datos manualmente.' : '❌ Error al consultar el propietario');
    } finally { setBuscandoProp(false); }
  };

  const buscarAcreedor = async () => {
    if (!f.acreedor_documento) { alert('⚠️ Ingrese el documento del acreedor'); return; }
    setBuscandoAcr(true);
    try {
      const res = await axios.get(`/api/tramites/owner/${f.acreedor_documento}?tipo_documento_id=${f.acreedor_tipo_documento}`);
      if (res.data) {
        set('acreedor_nombre', res.data.propietario || f.acreedor_nombre);
        set('acreedor_estado_persona', res.data.estado_persona || null);
      }
    } catch (err) {
      alert(err.response?.status === 404 ? 'ℹ️ Acreedor no encontrado. Ingrese el nombre manualmente.' : '❌ Error al consultar el acreedor');
    } finally { setBuscandoAcr(false); }
  };

  const archivoValido = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!EXT_OK.includes(ext)) { alert(`❌ "${file.name}" no es PDF ni imagen.`); return false; }
    if (file.size > 6 * 1024 * 1024) { alert(`❌ "${file.name}" supera los 6MB`); return false; }
    return true;
  };

  const enviar = async () => {
    if (!info || !info.puede_crear) { alert('⚠️ Consulte primero una placa válida (matriculada y con SOAT vigente)'); return; }
    if (!f.propietario.trim() || !f.documento.trim()) { alert('⚠️ Complete los datos del propietario'); return; }
    if (!f.acreedor_nombre.trim() || !f.acreedor_documento.trim()) { alert('⚠️ Complete nombre e identificación del acreedor prendario'); return; }
    if (!mandato) { alert('⚠️ Debe adjuntar el contrato de prenda'); return; }
    const total = (mandato?.size || 0) + otros.reduce((a, x) => a + x.size, 0);
    if (total > 15 * 1024 * 1024) { alert('❌ La suma de los archivos no puede superar 15MB'); return; }

    setEnviando(true);
    const data = new FormData();
    data.append('transito_id', info.transito.id);
    data.append('tipo_tramite_id', info.tipo_tramite_id);
    data.append('placa_referencia', info.placa);
    data.append('datos_adicionales', JSON.stringify({ ...f, placa_referencia: info.placa }));
    data.append('Contrato_Mandato', mandato);
    otros.forEach(o => data.append('Otros_Documentos', o));
    try {
      await axios.post('/api/tramites', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('✅ Inscripción de prenda radicada correctamente');
      onSuccess(); onClose();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo crear el trámite'));
    } finally { setEnviando(false); }
  };

  const req = <span style={{ color: 'red' }}>*</span>;
  const v = info?.vehiculo || {};
  const soatOk = info?.soat?.estado === 'vigente';

  return (
    <div>
      <div className="form-section">
        <div className="form-section-header">
          <div className="form-section-icon">🚗</div>
          <h4 className="form-section-title">PLACA DE LA INSCRIPCIÓN DE PRENDA</h4>
        </div>
        <small style={{ display: 'block', margin: '8px 0' }}>
          El vehículo debe tener una matrícula ya ejecutada en el aplicativo y el SOAT validado en esa matrícula.
        </small>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input type="text" value={placa} maxLength={7} placeholder="PLACA"
            onChange={(e) => { setPlaca(e.target.value.toUpperCase()); setInfo(null); setErrorPlaca(''); }}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), consultarPlaca())}
            style={{ flex: 1, textTransform: 'uppercase', fontWeight: 'bold' }} />
          <button type="button" className="btn-action btn-modules" onClick={consultarPlaca} disabled={consultando}
            style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}>
            {consultando ? '⏳ CONSULTANDO...' : 'CONSULTAR'}
          </button>
        </div>
        {errorPlaca && <div className="error-banner" style={{ marginTop: '10px' }}>{errorPlaca}</div>}
        {info && (
          <div style={{ marginTop: '12px', padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'var(--secondary-soft)', fontSize: '13px' }}>
            <div><b>Tránsito:</b> {info.transito?.nombre}</div>
            <div><b>Matrícula origen:</b> #{info.origen?.tramite_id}</div>
            <div><b>Vehículo:</b> {Object.entries(v).filter(([, val]) => val && typeof val !== 'object').map(([k, val]) => `${k}: ${val}`).join(' · ') || 'sin datos'}</div>
            <div><b>SOAT:</b> {soatOk
              ? `✅ vigente${info.soat.fecha_fin ? ' hasta ' + info.soat.fecha_fin : ''} (validado en la matrícula)`
              : '❌ SOAT no validado en la matrícula'}</div>
            {info.cupl_existente && (
              <div style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '6px', background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46', fontWeight: '600' }}>
                ✅ Se detectó el CUPL {info.cupl_existente.numero} de inscripción de prenda ({info.cupl_existente.pagado ? 'ya pagado' : 'pendiente de pago'}).
                {info.cupl_existente.pagado
                  ? ' Al radicar, el trámite quedará listo para finalizar.'
                  : ' Al radicar, quedará con el CUPL generado, pendiente de pago.'}
              </div>
            )}
            {(info.bloqueos || []).map((b, i) => (
              <div key={i} className="error-banner" style={{ marginTop: '8px' }}>{b}</div>
            ))}
          </div>
        )}
      </div>

      {info && info.puede_crear && (
        <>
          <div className="form-section">
            <div className="form-section-header">
              <div className="form-section-icon">👤</div>
              <h4 className="form-section-title">DATOS DEL PROPIETARIO</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginTop: '12px' }}>
              <div className="form-group">
                <label>TIPO DOC. {req}</label>
                <select value={f.tipo_documento} onChange={(e) => set('tipo_documento', e.target.value)}>
                  {TIPOS_DOC.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>NRO DE DOCUMENTO {req}</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="text" name="documento" value={f.documento} onChange={up} style={{ flex: 1 }} />
                  <button type="button" className="btn-action btn-modules" onClick={buscarPropietario} disabled={buscandoProp}
                    style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}>{buscandoProp ? '⏳' : 'CONSULTAR'}</button>
                </div>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label>NOMBRE / RAZÓN SOCIAL {req}</label>
                <input type="text" name="propietario" value={f.propietario} onChange={up} /></div>
              <div className="form-group"><label>CORREO</label>
                <input type="email" value={f.correo} onChange={(e) => set('correo', e.target.value)} /></div>
              <div className="form-group"><label>TELÉFONO</label>
                <input type="text" name="telefono" value={f.telefono} onChange={up} /></div>
              <div className="form-group"><label>DIRECCIÓN</label>
                <input type="text" name="direccion" value={f.direccion} onChange={up} /></div>
            </div>
            {f.tipo_documento === 'NIT' && (
              <div className="form-grid">
                <div className="form-group"><label>REPRESENTANTE LEGAL</label>
                  <input type="text" name="representante_legal_nombre" value={f.representante_legal_nombre} onChange={up} /></div>
                <div className="form-group"><label>DOC. REPRESENTANTE LEGAL</label>
                  <input type="text" name="representante_legal_documento" value={f.representante_legal_documento} onChange={up} /></div>
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <div className="form-section-icon">🏦</div>
              <h4 className="form-section-title">ACREEDOR PRENDARIO</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginTop: '12px' }}>
              <div className="form-group">
                <label>TIPO DOC. {req}</label>
                <select value={f.acreedor_tipo_documento} onChange={(e) => set('acreedor_tipo_documento', e.target.value)}>
                  {TIPOS_DOC.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>ID DEL ACREEDOR {req}</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="text" name="acreedor_documento" value={f.acreedor_documento} onChange={up} style={{ flex: 1 }} />
                  <button type="button" className="btn-action btn-modules" onClick={buscarAcreedor} disabled={buscandoAcr}
                    style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}>{buscandoAcr ? '⏳' : 'CONSULTAR'}</button>
                </div>
              </div>
            </div>
            <div className="form-group"><label>NOMBRE DEL ACREEDOR {req}</label>
              <input type="text" name="acreedor_nombre" value={f.acreedor_nombre} onChange={up} /></div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <div className="form-section-icon">📎</div>
              <h4 className="form-section-title">DOCUMENTOS</h4>
            </div>
            <div className="form-group">
              <label>CONTRATO DE PRENDA {req}</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => { const x = e.target.files[0]; if (x && archivoValido(x)) setMandato(x); else { e.target.value = ''; setMandato(null); } }} />
            </div>
            <div className="form-group">
              <label>OTROS DOCUMENTOS (opcional, puede seleccionar varios)</label>
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => { const xs = Array.from(e.target.files); if (xs.every(archivoValido)) setOtros(xs); else { e.target.value = ''; setOtros([]); } }} />
            </div>
            <div className="form-group">
              <label>OBSERVACIONES</label>
              <textarea name="observaciones" value={f.observaciones} onChange={up} rows={2} />
            </div>
          </div>
        </>
      )}

      <div className="modal-actions" style={{ padding: '0 20px 20px 20px' }}>
        <button type="button" onClick={onClose} className="btn-secondary" disabled={enviando}>cancelar</button>
        <button type="button" onClick={enviar} className="btn-primary" disabled={enviando || !info || !info.puede_crear}
          style={{ padding: '10px 25px', fontSize: '13px', fontWeight: '800' }}>
          {enviando ? '🚀 ENVIANDO...' : '🚀 RADICAR INSCRIPCIÓN'}
        </button>
      </div>
    </div>
  );
};

export default FormularioInscripcionPrenda;
